import React, { useEffect, useState } from 'react';
import { FileText, X, Maximize2 } from 'lucide-react';
import { DOCUMENTATION_SECTIONS } from '../../config/architectureConfig';

// Documentation tab — last step of the Architecture flow.
// Renders artefacts (processes, data discoveries, frameworks, architecture)
// as a 3-up grid of small previews. Clicking a card with an image opens
// a fullscreen lightbox so the user can read the diagram at full size.

function DiagramCard({ diagram, onOpen }) {
  const { title, caption, src, alt } = diagram;
  const clickable = Boolean(src);

  return (
    <figure
      onClick={clickable ? () => onOpen(diagram) : undefined}
      className={`group border border-gray-200 rounded-lg bg-white overflow-hidden flex flex-col ${
        clickable ? 'cursor-zoom-in hover:border-gray-300 hover:shadow-sm transition' : ''
      }`}
    >
      <figcaption className="px-3 py-2 border-b border-gray-100">
        <div className="text-xs font-semibold text-gray-800 truncate">{title}</div>
        {caption && (
          <div className="text-[11px] text-gray-500 mt-0.5 leading-snug line-clamp-2">{caption}</div>
        )}
      </figcaption>
      <div className="relative aspect-[4/3] bg-gray-50 flex items-center justify-center">
        {src ? (
          <>
            <img
              src={src}
              alt={alt || title}
              className="w-full h-full object-contain p-1.5"
            />
            <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/30 transition">
              <div className="opacity-0 group-hover:opacity-100 transition flex items-center gap-1.5 px-2 py-1 rounded-md bg-white/95 text-gray-700 text-[11px] font-medium shadow-sm">
                <Maximize2 className="w-3 h-3" strokeWidth={2} />
                View full size
              </div>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center gap-1.5 text-gray-400">
            <FileText className="w-6 h-6" strokeWidth={1.25} />
            <span className="text-[10px] uppercase tracking-wide">Placeholder</span>
          </div>
        )}
      </div>
    </figure>
  );
}

function Lightbox({ diagram, onClose }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  if (!diagram) return null;

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-50 bg-black/75 flex items-center justify-center p-6 backdrop-blur-sm"
    >
      <button
        onClick={onClose}
        aria-label="Close"
        className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition"
      >
        <X className="w-5 h-5" />
      </button>
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-lg overflow-hidden max-w-[95vw] max-h-[90vh] flex flex-col"
      >
        <div className="overflow-auto p-4 flex-1">
          <img
            src={diagram.src}
            alt={diagram.alt || diagram.title}
            className="max-w-full h-auto mx-auto"
          />
        </div>
        <div className="px-4 py-2.5 border-t border-gray-100 bg-white">
          <div className="text-sm font-medium text-gray-800">{diagram.title}</div>
          {diagram.caption && (
            <div className="text-xs text-gray-500 mt-0.5">{diagram.caption}</div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function DocumentationTab() {
  const [openDiagram, setOpenDiagram] = useState(null);

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-base font-semibold text-gray-800 mb-1">Documentation</h3>
        <p className="text-xs text-gray-500">
          Artefacts captured across discovery and engagement — processes, data
          discoveries, frameworks &amp; workflows, and architecture diagrams.
          Click any preview to view at full size.
        </p>
      </div>

      {DOCUMENTATION_SECTIONS.map((section) => (
        <section key={section.id} className="space-y-2">
          <div className="flex items-baseline justify-between gap-3">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
              {section.title}
            </div>
            {section.subtitle && (
              <div className="text-[11px] text-gray-400 truncate">{section.subtitle}</div>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {section.diagrams.map((diagram) => (
              <DiagramCard key={diagram.id} diagram={diagram} onOpen={setOpenDiagram} />
            ))}
          </div>
        </section>
      ))}

      <Lightbox diagram={openDiagram} onClose={() => setOpenDiagram(null)} />
    </div>
  );
}
